import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const WISIntegrationSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  const integrations = [
    {
      id: 1,
      name: {
        en: "Production Planning",
        hi: "उत्पादन योजना"
      },
      description: {
        en: "Seamless integration with production schedules and material requirements",
        hi: "उत्पादन कार्यक्रम और मैटेरियल आवश्यकताओं के साथ सहज एकीकरण"
      },
      icon: "Settings",
      benefits: [
        {
          en: "Real-time material availability",
          hi: "रियल-टाइम मैटेरियल उपलब्धता"
        },
        {
          en: "Automated material allocation",
          hi: "स्वचालित मैटेरियल आवंटन"
        },
        {
          en: "Production delay alerts",
          hi: "उत्पादन देरी अलर्ट"
        }
      ]
    },
    {
      id: 2,
      name: {
        en: "Sales Module",
        hi: "बिक्री मॉड्यूल"
      },
      description: {
        en: "Direct connection with sales orders and customer delivery schedules",
        hi: "बिक्री आदेश और ग्राहक डिलीवरी कार्यक्रम के साथ सीधा संबंध"
      },
      icon: "ShoppingCart",
      benefits: [
        {
          en: "Order fulfillment tracking",
          hi: "ऑर्डर पूर्ति ट्रैकिंग"
        },
        {
          en: "Customer delivery updates",
          hi: "ग्राहक डिलीवरी अपडेट"
        },
        {
          en: "Stock availability checks",
          hi: "स्टॉक उपलब्धता जांच"
        }
      ]
    },
    {
      id: 3,
      name: {
        en: "Quality Control",
        hi: "गुणवत्ता नियंत्रण"
      },
      description: {
        en: "Integration with quality testing and certification processes",
        hi: "गुणवत्ता परीक्षण और प्रमाणन प्रक्रियाओं के साथ एकीकरण"
      },
      icon: "Award",
      benefits: [
        {
          en: "Quality batch tracking",
          hi: "गुणवत्ता बैच ट्रैकिंग"
        },
        {
          en: "Defect material isolation",
          hi: "दोषपूर्ण मैटेरियल अलगाव"
        },
        {
          en: "Quality reports integration",
          hi: "गुणवत्ता रिपोर्ट एकीकरण"
        }
      ]
    }
  ];

  const dataFlow = [
    {
      step: 1,
      title: {
        en: "Order Receipt",
        hi: "ऑर्डर प्राप्ति"
      },
      description: {
        en: "Sales order triggers material requirement",
        hi: "बिक्री आदेश मैटेरियल आवश्यकता को ट्रिगर करता है"
      }
    },
    {
      step: 2,
      title: {
        en: "Inventory Check",
        hi: "इन्वेंटरी जांच"
      },
      description: {
        en: "WIS checks real-time availability",
        hi: "WIS रियल-टाइम उपलब्धता की जांच करता है"
      }
    },
    {
      step: 3,
      title: {
        en: "Production Planning",
        hi: "उत्पादन योजना"
      },
      description: {
        en: "Automatic material allocation to production",
        hi: "उत्पादन के लिए स्वचालित मैटेरियल आवंटन"
      }
    },
    {
      step: 4,
      title: {
        en: "Quality Verification",
        hi: "गुणवत्ता सत्यापन"
      },
      description: {
        en: "Quality checks before dispatch",
        hi: "डिस्पैच से पहले गुणवत्ता जांच"
      }
    }
  ];

  const t = (text) => text[currentLanguage] || text.en;

  return (
    <section className="py-20 bg-gradient-to-b from-surface-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {currentLanguage === 'en' ?'Seamless ERP Integration' :'सहज ERP एकीकरण'
            }
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentLanguage === 'en' ?'WIS seamlessly integrates with all ERP modules, ensuring unified data flow and real-time synchronization across your entire aluminum manufacturing operation' :'WIS सभी ERP मॉड्यूल के साथ सहजता से एकीकृत होता है, आपके पूरे एल्यूमीनियम निर्माण संचालन में एकीकृत डेटा फ्लो और रियल-टाइम सिंक्रोनाइज़ेशन सुनिश्चित करता है'
            }
          </p>
        </div>

        {/* Integration Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {integrations.map((integration) => (
            <div key={integration.id} className="bg-white rounded-xl shadow-lg p-8 border border-border hover:shadow-xl transition-all duration-300">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full mb-4">
                  <Icon name={integration.icon} size={32} />
                </div>
                <h3 className="text-xl font-semibold text-primary mb-2">
                  {t(integration.name)}
                </h3>
                <p className="text-text-secondary text-sm">
                  {t(integration.description)}
                </p>
              </div>

              <div className="space-y-3">
                {integration.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-success-100 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={12} color="var(--color-success)" />
                    </div>
                    <span className="text-sm text-text-secondary">{t(benefit)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Unified Data Flow Visualization */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-border">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-primary mb-4">
              {currentLanguage === 'en' ?'Unified Data Flow' :'एकीकृत डेटा फ्लो'
              }
            </h3>
            <p className="text-text-secondary">
              {currentLanguage === 'en' ?'See how data flows seamlessly across all integrated modules' :'देखें कि डेटा सभी एकीकृत मॉड्यूल में कैसे सहजता से प्रवाहित होता है'
              }
            </p>
          </div>

          {/* Data Flow Steps */}
          <div className="grid md:grid-cols-4 gap-8">
            {dataFlow.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full mb-4 font-bold">
                    {step.step}
                  </div>
                  <h4 className="font-semibold text-primary mb-2">
                    {t(step.title)}
                  </h4>
                  <p className="text-sm text-text-secondary">
                    {t(step.description)}
                  </p>
                </div>
                
                {/* Connection Arrow */}
                {index < dataFlow.length - 1 && (
                  <div className="hidden md:block absolute top-6 left-full w-8 h-0.5 bg-gradient-to-r from-primary to-info transform translate-x-2">
                    <div className="absolute right-0 top-0 w-0 h-0 border-l-4 border-l-info border-t-2 border-b-2 border-t-transparent border-b-transparent transform translate-x-1 -translate-y-1"></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Integration Benefits */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-primary mb-2">100%</div>
                <p className="text-sm text-text-secondary">
                  {currentLanguage === 'en' ? 'Data Accuracy' : 'डेटा सटीकता'}
                </p>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary mb-2">0 Sec</div>
                <p className="text-sm text-text-secondary">
                  {currentLanguage === 'en' ? 'Sync Delay' : 'सिंक देरी'}
                </p>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary mb-2">24/7</div>
                <p className="text-sm text-text-secondary">
                  {currentLanguage === 'en' ? 'Real-time Updates' : 'रियल-टाइम अपडेट'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WISIntegrationSection;